import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Lock, FileText, Database, Shield, Clock3, ImageIcon, Scale } from 'lucide-react';
import { Button, Section } from './Shared';
import { trackFb } from '../lib/fbpixel';
export function Hero() {
  const scrollToPricing = () => {
    trackFb('AddToCart', {
      content_name: 'Colección NOS MIENTEN',
      currency: 'ARS',
      value: 27990
    });
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative pt-12 pb-10 md:pt-20 md:pb-14 px-5 overflow-hidden" data-id="element-155">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-anthracite via-brand-dark to-brand-dark -z-10" data-id="element-156" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-brand-gold/5 blur-[120px] rounded-full -z-10" data-id="element-157" />

      <div className="max-w-5xl mx-auto flex flex-col items-center text-center" data-id="element-158">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-159">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-red/30 bg-brand-red/10 text-brand-red text-xs font-bold tracking-widest uppercase mb-6" data-id="element-160">
            <Lock className="w-3 h-3" data-id="element-161" />
            <span data-id="element-162">Acceso Clasificado</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-163">
            Durante más de 80 años se acumularon documentos, testimonios y casos
            que rara vez aparecen{' '}
            <span className="text-brand-gold" data-id="element-164">reunidos en un solo lugar.</span>
          </h1>

          <p className="text-base md:text-xl text-brand-muted max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-165">
            NOS MIENTEN reúne investigaciones, documentos desclasificados, casos
            militares, testimonios de pilotos y grandes enigmas históricos en
            una colección documental premium diseñada para quienes quieren
            analizar toda la información antes de sacar sus propias
            conclusiones.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative w-full max-w-4xl mx-auto mb-12" data-id="element-166">
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent z-10 h-full" data-id="element-167" />
          <img src="https://cdn.magicpatterns.com/uploads/g9b2hwXCqjNA6SvEzeo6ts/ChatGPT_Image_26_jun_2026,_18_39_00.png" alt="Colección Nos Mienten" className="w-full h-auto object-cover rounded-lg shadow-2xl relative z-0" data-id="element-168" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="w-full max-w-md mx-auto" data-id="element-169">
          <ul className="text-left space-y-3 mb-8 bg-brand-card/50 p-6 rounded-xl border border-brand-anthracite" data-id="element-170">
            {['Más de 80 años de investigaciones reunidas.', 'Casos militares, documentos y testimonios organizados.', '6 tomos + 3 investigaciones exclusivas.', 'Biblioteca documental premium.'].map((bullet, idx) => <li key={idx} className="flex items-center gap-3 text-sm md:text-base text-brand-offWhite" data-id="element-171">
                <CheckCircle2 className="w-5 h-5 text-brand-gold shrink-0" data-id="element-172" />
                <span data-id="element-173">{bullet}</span>
              </li>)}
          </ul>

          <Button fullWidth onClick={scrollToPricing} className="text-lg py-5" data-id="element-174">
            QUIERO ACCEDER AHORA
          </Button>
          <p className="text-xs text-brand-muted mt-4 text-center" data-id="element-175">
            Acceso inmediato • Formato digital • Bonos incluidos
          </p>
        </motion.div>
      </div>
    </section>;
}
const archiveContents = [{
  icon: FileText,
  title: 'Documentos desclasificados',
  desc: 'Expedientes, memorandos y material público presentado con el contexto necesario para interpretarlo.'
}, {
  icon: Shield,
  title: 'Casos militares investigados',
  desc: 'Incidentes, avistamientos y registros vinculados a fuerzas, pilotos y sistemas de detección.'
}, {
  icon: Clock3,
  title: 'Cronologías completas',
  desc: 'Los hechos ordenados para entender qué ocurrió, cuándo y por qué cada caso sigue siendo relevante.'
}, {
  icon: Database,
  title: 'Testimonios históricos',
  desc: 'Relatos, declaraciones y fuentes que ayudan a observar cada tema desde más de una perspectiva.'
}, {
  icon: ImageIcon,
  title: 'Fotografías y archivos',
  desc: 'Material visual y referencias documentales que acompañan el recorrido de cada investigación.'
}, {
  icon: Scale,
  title: 'Hipótesis comparadas',
  desc: 'Diferentes lecturas y explicaciones presentadas sin verdades cerradas, para que saques tus conclusiones.'
}];
export function ArchiveContents() {
  return <Section darker data-id="element-176">
      <div className="text-center max-w-3xl mx-auto mb-8" data-id="element-177">
        <p className="text-xs font-bold tracking-[0.18em] text-brand-gold uppercase mb-3" data-id="element-178">
          Dentro del Archivo
        </p>
        <h2 className="text-2xl md:text-4xl font-bold text-white" data-id="element-179">
          ¿Qué vas a encontrar dentro del Archivo?
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-id="element-180">
        {archiveContents.map(item => <article key={item.title} className="bg-brand-anthracite border border-brand-card rounded-xl p-5" data-id="element-181">
            <item.icon className="w-5 h-5 text-brand-gold mb-4" data-id="element-182" />
            <h3 className="text-base font-bold text-white mb-2" data-id="element-183">
              {item.title}
            </h3>
            <p className="text-sm text-brand-muted leading-relaxed" data-id="element-184">
              {item.desc}
            </p>
          </article>)}
      </div>
    </Section>;
}
export function Tension() {
  return <Section data-id="element-185">
      <div className="text-center max-w-3xl mx-auto" data-id="element-186">
        <h2 className="text-2xl md:text-4xl font-bold text-white mb-6" data-id="element-187">
          Nos dieron versiones incompletas.
        </h2>
        <div className="space-y-5 text-brand-muted text-base md:text-lg leading-relaxed" data-id="element-188">
          <p data-id="element-189">
            Durante décadas conocimos historias aisladas: Roswell, Área 51,
            pilotos militares, fenómenos inexplicables y civilizaciones
            perdidas. Todo parecía desconectado.
          </p>
          <p className="text-white font-medium text-lg md:text-xl border-l-2 border-brand-gold pl-4 text-left" data-id="element-190">
            NOS MIENTEN organiza todas esas investigaciones dentro de una misma
            colección para que puedas entender el contexto completo y descubrir
            conexiones que rara vez aparecen explicadas juntas.
          </p>
        </div>
      </div>
    </Section>;
}
export function WhatThisIs() {
  return <Section data-id="element-191">
      <div className="max-w-2xl mx-auto text-center" data-id="element-192">
        <h2 className="text-2xl md:text-4xl font-bold text-white mb-6" data-id="element-193">
          Esto no es un libro que vas a leer una sola vez.
        </h2>
        <p className="text-brand-muted mb-8 text-base md:text-lg" data-id="element-194">
          Es una biblioteca documental creada para consultar una y otra vez.
          Cada volumen desarrolla un tema diferente y fue pensado para ayudarte
          a comprender investigaciones que normalmente se encuentran dispersas
          entre cientos de fuentes distintas.
        </p>
        <div className="grid grid-cols-2 gap-4 max-w-md mx-auto" data-id="element-195">
          {[{
          icon: Database,
          text: 'Estructura'
        }, {
          icon: FileText,
          text: 'Contexto'
        }, {
          icon: Lock,
          text: 'Investigación'
        }, {
          icon: CheckCircle2,
          text: 'Diseño editorial'
        }].map((item, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-brand-offWhite bg-brand-dark p-3 rounded-lg border border-brand-card" data-id="element-196">
              <item.icon className="w-4 h-4 text-brand-gold" data-id="element-197" />
              <span data-id="element-198">{item.text}</span>
            </div>)}
        </div>
      </div>
    </Section>;
}
export function MainCollection() {
  return <Section darker className="text-center" data-id="element-199">
      <h2 className="text-xs font-bold tracking-widest text-brand-gold uppercase mb-3" data-id="element-200">
        El núcleo del archivo
      </h2>
      <h3 className="text-2xl md:text-4xl font-bold text-white mb-8" data-id="element-201">
        Lo que recibís al entrar al archivo
      </h3>

      <div className="bg-brand-anthracite border border-brand-card rounded-2xl p-6 md:p-10 max-w-4xl mx-auto text-left relative overflow-hidden" data-id="element-202">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/5 blur-3xl rounded-full" data-id="element-203" />

        <div className="flex flex-col md:flex-row gap-8 items-center relative z-10" data-id="element-204">
          <div className="w-full md:w-1/3" data-id="element-205">
            <img src="https://cdn.magicpatterns.com/uploads/g9b2hwXCqjNA6SvEzeo6ts/ChatGPT_Image_26_jun_2026,_18_39_00.png" alt="Nos Mienten Cover" className="w-full h-auto rounded shadow-xl object-cover object-left h-[300px]" data-id="element-206" />
          </div>
          <div className="w-full md:w-2/3" data-id="element-207">
            <h4 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-208">
              NOS MIENTEN: El Archivo de los No Contactados
            </h4>
            <p className="text-brand-gold text-sm font-medium mb-4" data-id="element-209">
              LA OBRA CENTRAL DE LA COLECCIÓN
            </p>
            <p className="text-brand-muted text-base leading-relaxed" data-id="element-210">
              Una colección documental premium dividida en 6 tomos para recorrer
              casos, fuentes, testimonios y debates desde sus primeros registros
              hasta las revelaciones recientes. Pensada como una obra de
              consulta que organiza material disperso para que cada lector pueda
              analizarlo y formar sus propias conclusiones.
            </p>
          </div>
        </div>
      </div>
    </Section>;
}